import { AccountInfo, Configuration, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { Role } from '../models';

const USERNAME_LOCAL_STORAGE_KEY = 'EMP_MSAL_USERNAME';
const ACCESS_TOKEN_LOCAL_STORAGE_KEY = 'EMP_MSAL_ACCESS_TOKEN';

export const msalConfig: Configuration = {
	auth: {
		clientId: '8e75f5b9-0f69-49d5-aef4-3ca5e56ffaf8',
		authority: 'https://login.microsoftonline.com/etmdm.onmicrosoft.com',
		redirectUri: window.location.origin,
		navigateToLoginRequestUrl: false
	},
	cache: {
		cacheLocation: 'localStorage', // This configures where your cache will be stored
		storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
	}
};

export const userAgentApplication = new PublicClientApplication(msalConfig);
const _initPromise = userAgentApplication.initialize();

export const loginRequest = {
	scopes: ['openid', 'profile', 'offline_access'],
	prompt: 'select_account'
};

export const silentRequest = {
	scopes: ['api://8e75f5b9-0f69-49d5-aef4-3ca5e56ffaf8/access_as_user']
};

function refreshPage() {
	window.location.reload();
}

export const loginAccount = async (): Promise<void> => {
	await _initPromise;
	await userAgentApplication.loginRedirect(loginRequest);
};

export const logoutAccount = async (): Promise<void> => {
	await _initPromise;
	const account = getCurrentAccount();
	if (account) {
		setCurrentUsername(null);
		await userAgentApplication.logoutRedirect();
	}
};

export const setCurrentAccount = (userName: string) => {
	setCurrentUsername(userName);
	refreshPage();
};

export const getAccount = (): AccountInfo | null => {
	let account: AccountInfo | null = null;
	const accounts = userAgentApplication.getAllAccounts();

	if (accounts.length > 0) {
		account = getCurrentAccount();
	if (!account) {
			// return the first account that we have
			account = accounts[0];
			setCurrentUsername(account.username);
		}
	}

	return account;
};

export const getCurrentAccount = (): AccountInfo | null => {
	const accounts = userAgentApplication.getAllAccounts();
	const username = getCurrentUsername();
	return username ? userAgentApplication.getAccountByUsername(username) : accounts[0];
};

export const getAllAccounts = (): AccountInfo[] => {
	return userAgentApplication.getAllAccounts();
};

export const getTokens = async (account: AccountInfo): Promise<void | { idToken: string; accessToken: string }> => {
	await _initPromise;
	const request = {
		...silentRequest,
		account: account
	};
	return userAgentApplication
		.acquireTokenSilent(request)
		.then(response => {
			setAccessToken(response.accessToken);
			return {
				idToken: response.idToken,
				accessToken: response.accessToken
			};
		})
		.catch(async error => {
			console.log('acquireTokenSilent:Error', error);
			if (error instanceof InteractionRequiredAuthError) {
				userAgentApplication.acquireTokenRedirect(request);
			}
		});
};

export const getCurrentUsername = (): string | null => {
	return localStorage.getItem(USERNAME_LOCAL_STORAGE_KEY);
};

export const getCurrentAccessToken = (): string | null => {
	return localStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
};

export const setCurrentUsername = (userName: string | null): void => {
	if (userName) {
		localStorage.setItem(USERNAME_LOCAL_STORAGE_KEY, userName);
	} else {
		localStorage.removeItem(USERNAME_LOCAL_STORAGE_KEY);
	}
};

const setAccessToken = (accessToken: string | null): void => {
	if (accessToken) {
		localStorage.setItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY, accessToken);
	} else {
		localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
	}
};

export const getAccessToken = async (): Promise<string> => {
	const account = getCurrentAccount();
	if (!account) throw new Error('Failed To Retrieve Access Token: Account Is Null');
	const tokens = await getTokens(account);
	if (!tokens) throw new Error('Failed To Retrieve Access Token: Tokens Is Null');
	const { accessToken } = tokens;
	if (!accessToken) throw new Error('Failed To Retrieve Access Token: Access Token Is Null');
	return accessToken;
};

export const getRoles = async (): Promise<Array<Role>> => {
	const token = getCurrentAccessToken() || (await getAccessToken());
	if (!token) {
		throw new Error('Failed To Retrieve Access Token: Access Token Is Null');
	}
	const parsed = jwtDecode(token) as IAccessToken;
	return parsed.roles;
};

export interface IAccessToken {
	aud: string;
	businessUnit: string[];
	clientId: string;
	exp: number;
	family_name: string;
	given_name: string;
	iat: number;
	iss: string;
	nbf: number;
	permission: string[];
	roles: Role[];
	sub: string;
	unique_name: string;
}
